import { useMemo, useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Skeleton,
  Typography,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { LockOutlined } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../reduxStore/hooks";
import { subValues } from "../../../utils/constants/subValues";
import { setPremiumModalStatus } from "../../../reduxStore/features/authSlice";
import { useNavigate } from "react-router-dom";
import { QUERY_FILE } from "../../../utils/constants/urlParamsConstants";
import { DOMAIN_CONFIG_OBJ } from "../../../utils/axiosInstance";
import { QBOOK_DOMAIN_VALUES } from "../../../utils/constants/domainNameValues";

const NavigationMenu = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { billing, isLoading, userData } = useAppSelector(
    (state) => state.auth
  );
  const { blogSpecificInfo: articleSpecificInfo } = useAppSelector(
    (state) => state.learn
  );
  const { navigationArray, loadNavigation } = useAppSelector(
    (state) => state.navigation
  );
  const [openChapters, setOpenChapters] = useState<{ [key: string]: boolean }>(
    {}
  );
  const searchParams = new URLSearchParams(window.location.search);
  const fileId = searchParams.get(QUERY_FILE);

  const isAuthorized = useMemo(() => {
    if (userData?._id && articleSpecificInfo?.visibility === "private") {
      // if user data is present and blog is private, then user is authorized
      // bcz the article content loading api will return 200 only if user is authorized to access the content
      // if not a 403 error will be thrown, and its handled in the thunk
      return true;
    }
    return DOMAIN_CONFIG_OBJ.domainName === QBOOK_DOMAIN_VALUES.qbraid
      ? Object.values(subValues).includes(
          billing?.billing?.activePlanName?.toLowerCase() || ""
        )
      : true;
  }, [
    articleSpecificInfo?.visibility,
    billing?.billing?.activePlanName,
    userData?._id,
  ]);

  const handleChapterClick = (chapterKey: string, chapterIndex: number) => {
    if (isLoading || loadNavigation) return;

    if (!isAuthorized && chapterIndex > 1) {
      dispatch(setPremiumModalStatus(true));
      return;
    }
    setOpenChapters((prev) => ({
      ...prev,
      [chapterKey]: !prev[chapterKey],
    }));
  };

  const handleNavClick = (navKey: any) => {
    if (!navKey) {
      console.error("No navKey provided to handleNavClick");
      return;
    }
    navigate(`/learn/${navKey}`);
  };

  return (
    <Box
      sx={{
        // maxWidth: '80%',
        backgroundColor: "var(--bg-primary-clr1)",
        color: "var(--text-primary-clr1)",
        "& .MuiTypography-root": {
          fontSize: "14px",
        },
      }}
      cy-data="qbook-sidebar-navigation-menu"
    >
      {!loadNavigation && navigationArray.length > 0 ? (
        <List component="nav">
          {navigationArray.map((chapter, chapterIndex) => (
            <Box key={chapter.key}>
              <ListItem
                button
                onClick={(e) => {
                  e.preventDefault();
                  handleChapterClick(chapter.key, chapterIndex);
                }}
                sx={{
                  fontSize: "14px",
                  py: "8px",
                  opacity:
                    isLoading || (!isAuthorized && chapterIndex > 1) ? 0.5 : 1,
                  "&:hover": {
                    backgroundColor:
                      isLoading || (!isAuthorized && chapterIndex > 1)
                        ? ""
                        : "rgba(255, 255, 255, 0.08)",
                    color:
                      isLoading || (!isAuthorized && chapterIndex > 1)
                        ? ""
                        : "var(--accent-clr1)",
                  },
                }}
                cy-data="qbook-course-sidebar-nav-container"
              >
                <ListItemText
                  sx={{
                    maxWidth: "100%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      {isLoading && chapterIndex > 1 ? (
                        <Skeleton width={15} height={20} />
                      ) : (
                        !isAuthorized &&
                        chapterIndex > 1 && (
                          <LockOutlined
                            sx={{ width: "15px", height: "15px" }}
                          />
                        )
                      )}
                      <Typography
                        sx={{
                          fontSize: "14px",
                          maxWidth: "130px",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {chapter.label}
                      </Typography>
                    </Box>
                  }
                />
                <Box
                  sx={{
                    perspective: "1000px",
                    display: "flex",
                    alignItems: "center",
                    "& > *": {
                      transition:
                        "transform 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: openChapters[chapter.key]
                        ? "rotateX(180deg)"
                        : "rotateX(0deg)",
                      transformStyle: "preserve-3d",
                    },
                  }}
                >
                  <ExpandMore sx={{ fontSize: "18px", fontWeight: 600 }} />
                </Box>
              </ListItem>
              <Collapse
                in={openChapters[chapter.key]}
                timeout="auto"
                unmountOnExit
              >
                <List component="div">
                  {chapter?.children?.map((child) => (
                    <ListItem
                      key={child.key}
                      button
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(child.key);
                      }}
                      sx={{
                        pl: 4,
                        py: "8px",
                        backgroundColor:
                          fileId && child.key.includes(fileId)
                            ? "rgba(225, 179, 255, 0.51)"
                            : "",
                        borderRight:
                          fileId && child.key.includes(fileId)
                            ? "2px solid var(--accent-clr1)"
                            : "",
                        "&:hover": {
                          color: "var(--accent-clr1)",
                          backgroundColor:
                            fileId && child.key.includes(fileId)
                              ? "rgba(225, 179, 255, 0.51)"
                              : "rgba(255, 255, 255, 0.08)",
                        },
                      }}
                      cy-data="qbook-course-sidebar-nav-child-item"
                    >
                      <ListItemText primary={child.label} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
      ) : (
        <div style={{ width: "100%" }}>
          <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
          <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
          <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
          <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
        </div>
      )}
    </Box>
  );
};

export default NavigationMenu;
